import React, { useState, useEffect, useCallback } from 'react';
import authedFetch from '../../lib/authedFetch';
import {
  Activity, CheckCircle2, XCircle, AlertTriangle, RefreshCw, Loader2,
  FileCode, Clock, TrendingUp, TrendingDown, ChevronDown, ChevronUp,
  Shield, Bug, FileWarning, Server
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine
} from 'recharts';
import { toast } from 'sonner';
import secureStorage from '../../lib/secureStorage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ScoreGauge({ score }) {
  const color = score >= 80 ? '#10b981' : score >= 60 ? '#f59e0b' : '#ef4444';
  const label = score >= 80 ? 'Healthy' : score >= 60 ? 'Needs Attention' : 'Critical';
  const circumference = 2 * Math.PI * 54;
  const filled = (score / 100) * circumference;

  return (
    <div className="flex flex-col items-center" data-testid="health-score-gauge">
      <svg width="140" height="140" viewBox="0 0 140 140">
        <circle cx="70" cy="70" r="54" fill="none" stroke="#e5e7eb" strokeWidth="10" />
        <circle
          cx="70" cy="70" r="54" fill="none" stroke={color} strokeWidth="10"
          strokeLinecap="round" strokeDasharray={`${filled} ${circumference}`}
          transform="rotate(-90 70 70)" style={{ transition: 'stroke-dasharray 1s ease' }}
        />
        <text x="70" y="65" textAnchor="middle" className="text-3xl font-bold" fill={color}>{score}</text>
        <text x="70" y="85" textAnchor="middle" className="text-xs" fill="#6b7280">/100</text>
      </svg>
      <Badge
        className={`mt-2 ${score >= 80 ? 'bg-emerald-100 text-emerald-700' : score >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`}
      >
        {label}
      </Badge>
    </div>
  );
}

function CheckCard({ title, icon: Icon, passed, details, issues, expanded, onToggle }) {
  const statusColor = passed ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50';
  const iconColor = passed ? 'text-emerald-600' : 'text-rose-600';

  return (
    <div className={`rounded-lg border-2 ${statusColor} overflow-hidden`} data-testid={`check-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <button
        onClick={onToggle}
        className="w-full p-4 flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${passed ? 'bg-emerald-100' : 'bg-rose-100'}`}>
            <Icon className={`h-5 w-5 ${iconColor}`} />
          </div>
          <div>
            <h4 className="font-semibold text-slate-900">{title}</h4>
            <p className="text-sm text-slate-600">{details}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {passed ? (
            <CheckCircle2 className="h-6 w-6 text-emerald-500" />
          ) : (
            <XCircle className="h-6 w-6 text-rose-500" />
          )}
          {issues?.length > 0 && (expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />)}
        </div>
      </button>
      {expanded && issues?.length > 0 && (
        <div className="px-4 pb-4 border-t border-slate-200 bg-white">
          <div className="mt-3 space-y-1 max-h-48 overflow-y-auto">
            {issues.map((issue, idx) => (
              <div key={`${issue.file}-${issue.line}-${idx}`} className="flex items-start gap-2 text-sm py-1 px-2 rounded hover:bg-slate-50">
                <Badge variant="outline" className={`text-xs shrink-0 ${issue.severity === 'error' ? 'border-rose-300 text-rose-600' : 'border-amber-300 text-amber-600'}`}>
                  {issue.severity === 'error' ? 'ERR' : 'WARN'}
                </Badge>
                <span className="text-slate-500 shrink-0 font-mono text-xs">{issue.file}:{issue.line}</span>
                <span className="text-slate-700">{issue.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function OversizedFilesCard({ fileData, expanded, onToggle }) {
  if (!fileData) return null;
  const { oversized_files, largest_files, total_files_scanned, threshold, passed } = fileData;

  return (
    <div className={`rounded-lg border-2 ${passed ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'} overflow-hidden`} data-testid="check-file-sizes">
      <button onClick={onToggle} className="w-full p-4 flex items-center justify-between text-left">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${passed ? 'bg-emerald-100' : 'bg-amber-100'}`}>
            <FileWarning className={`h-5 w-5 ${passed ? 'text-emerald-600' : 'text-amber-600'}`} />
          </div>
          <div>
            <h4 className="font-semibold text-slate-900">File Sizes</h4>
            <p className="text-sm text-slate-600">
              {oversized_files?.length || 0} files over {threshold} lines ({total_files_scanned} scanned)
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {passed ? <CheckCircle2 className="h-6 w-6 text-emerald-500" /> : <AlertTriangle className="h-6 w-6 text-amber-500" />}
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
      </button>
      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-200 bg-white">
          {oversized_files?.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-medium text-slate-500 mb-2">Oversized Files ({'>'}{threshold} lines)</p>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {oversized_files.map((f, idx) => (
                  <div key={f.file || `file-${idx}`} className="flex items-center gap-3">
                    <FileCode className="h-4 w-4 text-slate-400 shrink-0" />
                    <span className="font-mono text-sm text-slate-700 flex-1 truncate">{f.file}</span>
                    <div className="flex items-center gap-2">
                      <Progress value={Math.min((f.lines / 1500) * 100, 100)} className="w-20 h-2" />
                      <Badge className={`text-xs ${f.lines > 1200 ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>
                        {f.lines}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {largest_files?.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-medium text-slate-500 mb-2">Top 10 Largest Files</p>
              <div className="space-y-1">
                {largest_files.map((f, idx) => (
                  <div key={f.file || `file-${idx}`} className="flex items-center gap-2 text-sm">
                    <span className="text-slate-400 w-5 text-right">{idx + 1}.</span>
                    <span className="font-mono text-slate-600 flex-1 truncate">{f.file}</span>
                    <span className="text-slate-500 font-mono">{f.lines}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * FailingNowStrip — dense top-of-page callout listing every failed
 * check with its top offenders so engineering can dispatch fixes
 * without scrolling past the score gauge / summary cards. Clicking
 * any file path copies it to the clipboard (frontend doesn't have
 * an editor protocol handler — copy is the fastest way to paste
 * into a terminal `code <path>`). Self-hides when every check is
 * green, which is the win condition.
 */
function FailingNowStrip({ checks }) {
  const failures = [];

  const lintEntry = (key, label) => {
    const c = checks?.[key];
    if (!c || c.passed) return;
    const issues = (c.issues || []).filter(
      (i) => i.severity === 'error' || (c.error_count || 0) > 0,
    );
    failures.push({
      key, label, kind: 'lint',
      summary: `${c.error_count || 0} error${(c.error_count || 0) === 1 ? '' : 's'}, ${c.warning_count || 0} warning${(c.warning_count || 0) === 1 ? '' : 's'}`,
      items: (issues.length ? issues : c.issues || []).slice(0, 5).map((i) => ({
        primary: `${i.file}:${i.line}`,
        secondary: i.message,
        severity: i.severity,
        copyValue: i.file,
      })),
    });
  };

  lintEntry('python_lint', 'Python Lint (ruff)');
  lintEntry('js_lint', 'JavaScript Lint (ESLint)');

  const tests = checks?.tests;
  if (tests && !tests.passed) {
    failures.push({
      key: 'tests', label: 'Backend Tests (pytest)', kind: 'tests',
      summary: `${tests.tests_failed || 0} failing, ${tests.tests_passed || 0} passing`,
      items: (tests.failures || []).slice(0, 5).map((f) => ({
        primary: f.test || f.nodeid || f.name || 'unknown test',
        secondary: f.message || f.detail || '',
        severity: 'error',
        copyValue: f.test || f.nodeid || f.name || '',
      })),
    });
  }

  const fs = checks?.file_sizes;
  if (fs && !fs.passed) {
    const oversized = fs.oversized_files || [];
    failures.push({
      key: 'file_sizes', label: 'Oversized Files', kind: 'files',
      summary: `${oversized.length} files over ${fs.threshold} lines`,
      items: oversized.slice(0, 5).map((f) => ({
        primary: f.file,
        secondary: `${f.lines} lines`,
        severity: f.lines > 1200 ? 'error' : 'warning',
        copyValue: f.file,
      })),
    });
  }

  if (failures.length === 0) return null;

  const copyToClipboard = (value) => {
    if (!value) return;
    navigator.clipboard.writeText(value)
      .then(() => toast.success(`Copied "${value}"`))
      .catch(() => toast.error('Copy failed'));
  };

  return (
    <Card
      className="border-rose-300 bg-rose-50/60 ring-1 ring-rose-200"
      data-testid="code-health-failing-now"
    >
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2 text-rose-900">
          <AlertTriangle className="h-4 w-4 text-rose-600" />
          What's failing right now
          <Badge className="bg-rose-200 text-rose-900 ml-1" data-testid="failing-now-count">
            {failures.length} check{failures.length === 1 ? '' : 's'}
          </Badge>
        </CardTitle>
        <CardDescription className="text-rose-800/80">
          Tap any path to copy — paste into your terminal as <span className="font-mono">code &lt;path&gt;</span> to jump straight in.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {failures.map((f) => (
          <div key={f.key} data-testid={`failing-now-${f.key}`} className="bg-white border border-rose-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <XCircle className="h-4 w-4 text-rose-500" />
                {f.label}
              </h4>
              <span className="text-xs text-rose-700 font-medium">{f.summary}</span>
            </div>
            {f.items.length === 0 ? (
              <p className="text-xs text-slate-500 italic">
                Run the health check to see specific offenders.
              </p>
            ) : (
              <ul className="space-y-1">
                {f.items.map((it, idx) => (
                  <li key={`${f.key}-${idx}`}
                    className="flex items-start gap-2 text-xs px-2 py-1 rounded hover:bg-rose-50/50 cursor-pointer"
                    onClick={() => copyToClipboard(it.copyValue)}
                    data-testid={`failing-now-${f.key}-row-${idx}`}
                    title={`Click to copy ${it.copyValue}`}
                  >
                    <Badge variant="outline" className={`text-[10px] shrink-0 ${
                      it.severity === 'error'
                        ? 'border-rose-300 text-rose-700'
                        : 'border-amber-300 text-amber-700'
                    }`}>
                      {it.severity === 'error' ? 'ERR' : 'WARN'}
                    </Badge>
                    <span className="font-mono text-slate-700 break-all flex-1">{it.primary}</span>
                    <span className="text-slate-500 shrink-0">{it.secondary}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export function CodeHealthTab() {
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [expandedCheck, setExpandedCheck] = useState(null);

  const headers = {
    'Authorization': `Bearer ${secureStorage.getItem('session_token')}`,
    'Content-Type': 'application/json'
  };

  const fetchLatest = useCallback(async () => {
    try {
      const res = await authedFetch(`${API_URL}/api/admin/code-health/latest`, { headers });
      if (res.ok) {
        const data = await res.json();
        if (data.health_score !== null) setLatest(data);
      }
    } catch (e) { /* silent */ }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await authedFetch(`${API_URL}/api/admin/code-health/history?limit=30`, { headers });
      if (res.ok) setHistory(await res.json());
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    Promise.all([fetchLatest(), fetchHistory()]).finally(() => setLoading(false));
  }, [fetchLatest, fetchHistory]);

  const runHealthCheck = async () => {
    setRunning(true);
    try {
      const res = await authedFetch(`${API_URL}/api/admin/code-health/run`, {
        method: 'POST', headers
      });
      if (res.ok) {
        const data = await res.json();
        setLatest(data);
        toast.success(`Health check complete - Score: ${data.health_score}/100`);
        fetchHistory();
      } else {
        toast.error('Health check failed');
      }
    } catch (e) {
      toast.error('Health check failed');
    } finally {
      setRunning(false);
    }
  };

  const trendData = [...history].reverse().map(h => ({
    date: new Date(h.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    score: h.health_score,
    type: h.trigger_type === 'scheduled' ? 'auto' : 'manual'
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const checks = latest?.checks || {};

  return (
    <div className="space-y-6" data-testid="code-health-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Activity className="h-5 w-5 text-teal-600" />
            Code Health Dashboard
          </h2>
          <p className="text-sm text-slate-500">Automated lint, test, and file quality checks with daily monitoring</p>
        </div>
        <Button
          onClick={runHealthCheck}
          disabled={running}
          className="bg-teal-600 hover:bg-teal-700"
          data-testid="run-health-check-btn"
        >
          {running ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          {running ? 'Running...' : 'Run Health Check'}
        </Button>
      </div>

      {!latest ? (
        <Card className="border-dashed border-2 border-slate-300">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Shield className="h-12 w-12 text-slate-300 mb-3" />
            <p className="text-slate-500 font-medium">No health checks run yet</p>
            <p className="text-sm text-slate-400 mt-1">Click "Run Health Check" to get started</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* "What's failing right now" strip — only renders when at
              least one check is failing. Surfaces the top offenders
              per failed check so engineering can jump straight to
              the broken file without scrolling past the green-circle
              gauge. Click any file path to copy it to the clipboard. */}
          <FailingNowStrip checks={checks} />

          {/* Score + Summary Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="flex items-center justify-center">
              <CardContent className="pt-6">
                <ScoreGauge score={latest.health_score} />
              </CardContent>
            </Card>

            <Card className="md:col-span-2">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Server className="h-4 w-4 text-slate-500" />
                  Check Summary
                </CardTitle>
                <CardDescription>
                  {latest.summary?.checks_passed}/{latest.summary?.checks_total} checks passed
                  {' '}&#183;{' '}
                  {latest.trigger_type === 'scheduled' ? 'Scheduled' : 'Manual'} run
                  {' '}&#183;{' '}
                  {new Date(latest.timestamp).toLocaleString()}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { name: 'Python Lint', key: 'python_lint', icon: FileCode, detail: c => `${c?.error_count || 0}e / ${c?.warning_count || 0}w` },
                    { name: 'JS Lint', key: 'js_lint', icon: FileCode, detail: c => `${c?.error_count || 0}e / ${c?.warning_count || 0}w` },
                    { name: 'Tests', key: 'tests', icon: Bug, detail: c => `${c?.tests_passed || 0}p / ${c?.tests_failed || 0}f` },
                    { name: 'File Sizes', key: 'file_sizes', icon: FileWarning, detail: c => `${c?.oversized_count || 0} oversized` },
                  ].map(item => {
                    const check = checks[item.key];
                    return (
                      <div
                        key={item.key}
                        className={`p-3 rounded-lg border flex items-center gap-3 ${check?.passed ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}
                      >
                        <item.icon className={`h-4 w-4 ${check?.passed ? 'text-emerald-600' : 'text-rose-600'}`} />
                        <div>
                          <p className="text-sm font-medium text-slate-800">{item.name}</p>
                          <p className="text-xs text-slate-500">{item.detail(check)}</p>
                        </div>
                        {check?.passed ? <CheckCircle2 className="h-4 w-4 text-emerald-500 ml-auto" /> : <XCircle className="h-4 w-4 text-rose-500 ml-auto" />}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Check Cards */}
          <div className="space-y-3">
            <h3 className="text-base font-semibold text-slate-800">Detailed Results</h3>
            <CheckCard
              title="Python Lint (ruff)"
              icon={FileCode}
              passed={checks.python_lint?.passed}
              details={`${checks.python_lint?.error_count || 0} errors, ${checks.python_lint?.warning_count || 0} warnings`}
              issues={checks.python_lint?.issues || []}
              expanded={expandedCheck === 'python_lint'}
              onToggle={() => setExpandedCheck(expandedCheck === 'python_lint' ? null : 'python_lint')}
            />
            <CheckCard
              title="JavaScript Lint (ESLint)"
              icon={FileCode}
              passed={checks.js_lint?.passed}
              details={`${checks.js_lint?.error_count || 0} errors, ${checks.js_lint?.warning_count || 0} warnings`}
              issues={checks.js_lint?.issues || []}
              expanded={expandedCheck === 'js_lint'}
              onToggle={() => setExpandedCheck(expandedCheck === 'js_lint' ? null : 'js_lint')}
            />
            <CheckCard
              title="Backend Tests (pytest)"
              icon={Bug}
              passed={checks.tests?.passed}
              details={`${checks.tests?.tests_passed || 0} passed, ${checks.tests?.tests_failed || 0} failed, ${checks.tests?.tests_skipped || 0} skipped`}
              issues={(checks.tests?.failures || []).map(f => ({ file: '', line: '', message: f, severity: 'error' }))}
              expanded={expandedCheck === 'tests'}
              onToggle={() => setExpandedCheck(expandedCheck === 'tests' ? null : 'tests')}
            />
            <OversizedFilesCard
              fileData={checks.file_sizes}
              expanded={expandedCheck === 'file_sizes'}
              onToggle={() => setExpandedCheck(expandedCheck === 'file_sizes' ? null : 'file_sizes')}
            />
          </div>

          {/* Trend Chart */}
          {trendData.length > 1 && (
            <Card data-testid="health-trend-chart">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-teal-600" />
                  Health Score Trend
                </CardTitle>
                <CardDescription>
                  {trendData.length} checks recorded
                  {latest.health_score > (trendData[0]?.score || 0)
                    ? ' — improving'
                    : latest.health_score < (trendData[0]?.score || 0)
                    ? ' — declining'
                    : ' — stable'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trendData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} stroke="#94a3b8" />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                        formatter={(value) => [`${value}/100`, 'Score']}
                      />
                      <ReferenceLine y={80} stroke="#10b981" strokeDasharray="5 5" label={{ value: 'Healthy', fill: '#10b981', fontSize: 11 }} />
                      <ReferenceLine y={60} stroke="#f59e0b" strokeDasharray="5 5" label={{ value: 'Warning', fill: '#f59e0b', fontSize: 11 }} />
                      <Line
                        type="monotone" dataKey="score" stroke="#0d9488" strokeWidth={2.5}
                        dot={{ fill: '#0d9488', strokeWidth: 2, r: 4 }} activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* History Table */}
          {history.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="h-5 w-5 text-slate-500" />
                  Check History
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-slate-500">
                        <th className="pb-2 font-medium">Date</th>
                        <th className="pb-2 font-medium">Score</th>
                        <th className="pb-2 font-medium">Python</th>
                        <th className="pb-2 font-medium">JS</th>
                        <th className="pb-2 font-medium">Tests</th>
                        <th className="pb-2 font-medium">Files</th>
                        <th className="pb-2 font-medium">Trigger</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.slice(0, 15).map((h, idx) => {
                        const c = h.checks || {};
                        const scoreColor = h.health_score >= 80 ? 'text-emerald-600' : h.health_score >= 60 ? 'text-amber-600' : 'text-rose-600';
                        const prevScore = history[idx + 1]?.health_score;
                        const delta = prevScore != null ? h.health_score - prevScore : null;
                        return (
                          <tr key={h.timestamp || `hist-${idx}`} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-2 text-slate-600">{new Date(h.timestamp).toLocaleString()}</td>
                            <td className="py-2">
                              <span className={`font-bold ${scoreColor}`}>{h.health_score}</span>
                              {delta != null && delta !== 0 && (
                                <span className={`ml-1 text-xs ${delta > 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                                  {delta > 0 ? '+' : ''}{delta}
                                </span>
                              )}
                            </td>
                            <td className="py-2">{c.python_lint?.passed ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <XCircle className="h-4 w-4 text-rose-500" />}</td>
                            <td className="py-2">{c.js_lint?.passed ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <XCircle className="h-4 w-4 text-rose-500" />}</td>
                            <td className="py-2">{c.tests?.passed ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <XCircle className="h-4 w-4 text-rose-500" />}</td>
                            <td className="py-2">{c.file_sizes?.passed ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <AlertTriangle className="h-4 w-4 text-amber-500" />}</td>
                            <td className="py-2">
                              <Badge variant="outline" className="text-xs">
                                {h.trigger_type === 'scheduled' ? 'Auto' : 'Manual'}
                              </Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
